import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { Box, Flex, Icon, NakedButton } from '@qga/roo-ui/components';

import type { PackageOption } from '@shared/v2/utils/getPackageOptions';
import ToggleContent from '@shared/v2/components/ToggleContent';
import useFlyingFrom from '@shared/v2/hooks/useFlyingFrom';

import Button from '@/components/Button';
import Heading from '@/components/Heading';
import useProcuredOffer from '@/components/PackagePage/hooks/useProcuredOffer';
import Text from '@/components/Text';
import ViewDetailsModal from '@/components/ViewDetailsModal';
import { ORIGINS } from '@/constants';
import usePropertyContent from '@/hooks/usePropertyContent';
import { brandColors } from '@/lib/theme/colors';
import { Divider } from '@/v2/components/ui/Divider';
import {
  mapInclusion,
  PackageInclusions,
} from '@/v2/components/ui/PackageInclusions';
import { UI } from '@/v2/types';
import { getOriginName } from '@/v2/utils/getOriginName';
import {
  convertedHotelOfferInclusion,
  convertLeadInOfferInclusion,
} from '@/v2/utils/inclusions';
import { OtherPackageOffer } from '../../../../contexts/OtherPackageModalContext/utils';
import usePrependFlightInclusion from '../../../../hooks/usePrependFlightInclusion';
import OfferPrice from '../../../OfferPrice';
import LeadInOffer from '../LeadInOffer';
import UnavailableMessage from '../UnavailableMessage';

export interface PackageOptionProps {
  offer: OtherPackageOffer;
  isPreSearch?: boolean;
  route?: string;
  isNotSelectable?: boolean;
  isAlternativeModal?: boolean;
  onSelectPackageOption?: (args: {
    packageOption?: PackageOption;
    roomId?: string;
  }) => void;
}

const FlexGrow = styled(Flex)`
  max-width: 405px;
`;

const PackageOptionCard = ({
  offer: packageOffer,
  route,
  isPreSearch,
  isNotSelectable,
  onSelectPackageOption,
  isAlternativeModal,
}: PackageOptionProps) => {
  const propertyContent: UI.Property = usePropertyContent();
  const procuredOffer: UI.ProcuredOffer = useProcuredOffer();

  const { packageOption, leadInOffer, offer } = packageOffer;

  const handleViewPackageDetailsAndTerms =
    (show: Function) => (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      show();
    };

  const handleSelectPackageOption = () => {
    onSelectPackageOption?.({
      packageOption,
      roomId: offer?.roomType?.id,
    });
  };

  const baseInclusions = useMemo(() => {
    const leadInOfferInclusions = isPreSearch
      ? leadInOffer?.inclusions
      : offer?.leadInOffer?.inclusions;

    return (
      leadInOfferInclusions?.map(convertLeadInOfferInclusion) ??
      offer?.hotelOffer?.inclusions.map(convertedHotelOfferInclusion)
    );
  }, [offer, isPreSearch, leadInOffer?.inclusions]);

  let inclusions = usePrependFlightInclusion(baseInclusions) || [];
  if (isNotSelectable) {
    inclusions = (packageOption?.inclusions ?? []).map(mapInclusion);
  }

  const originCodes = useMemo(() => ORIGINS.map((o) => o.code), []);
  const { originCode } = useFlyingFrom(originCodes);
  const originName = useMemo(() => {
    if (route && originCode && route.startsWith(originCode)) {
      return getOriginName(originCode);
    }
  }, [originCode, route]);

  return (
    <Box
      padding={['16px', null, '24px']}
      borderRadius="md"
      border="1"
      borderColor={isNotSelectable ? 'transparent' : 'border'}
      overflow="hidden"
      width="100%"
      bg="background.card"
      boxShadow={isNotSelectable ? 'none' : 'elevation8'}
    >
      <Heading.h6
        color={isNotSelectable ? 'text.hint' : brandColors.iron}
        lineHeight={['20px', null, '22px']}
        fontSize={['16px', null, '18px']}
      >
        {packageOption?.title || 'Best available rate'}
      </Heading.h6>
      <Divider my={'16px'} />
      <Flex
        gap={'16px'}
        flexDirection={['column', null, 'row']}
        justifyContent={[null, null, 'space-between']}
      >
        <FlexGrow flexDirection="column" gap={'8px'}>
          {offer?.roomType && (
            <Flex flexDirection="column" gap="8px" mb="8px">
              <Text
                bold
                fontSize={['14px', null, '16px']}
                lineHeight={['20px', null, '24px']}
                letterSpacing="0.1px"
              >
                Room type:
              </Text>
              <Flex>
                <Icon
                  name="accommodation"
                  size={[22, null, 24]}
                  color="icon.inclusion"
                />
                <Text
                  ml="2"
                  color="text.body"
                  fontSize={['xs', null, 'sm']}
                  lineHeight={['20px', null, '24px']}
                  letterSpacing="0.1px"
                >
                  {offer?.roomType.name}
                </Text>
              </Flex>
            </Flex>
          )}
          {inclusions?.length > 0 && (
            <PackageInclusions
              color={isNotSelectable ? 'text.hint' : undefined}
              inclusions={inclusions}
              numberToShow={4}
              showSubtitle={false}
            />
          )}
          {procuredOffer && leadInOffer && (
            <ToggleContent
              toggle={(show) => (
                <NakedButton
                  textAlign="inherit"
                  fontSize={['14px', null, '16px']}
                  lineHeight={['20px', null, '24px']}
                  onClick={handleViewPackageDetailsAndTerms(show)}
                  color="ui.link"
                  hoverColor="ui.linkHover"
                >
                  <Text bold link fontSize={['xs', null, 'sm']}>
                    View package details and terms
                  </Text>
                </NakedButton>
              )}
              content={(close) => (
                <ViewDetailsModal
                  // @ts-ignore - bad type from JS
                  close={close}
                  title={`Holiday package to ${propertyContent.destination.title}
                        at ${propertyContent.name}`}
                  subtitle={procuredOffer?.name}
                  // @ts-ignore - bad type from JS
                  terms={procuredOffer?.terms}
                  inclusions={inclusions}
                  minNights={leadInOffer?.minNumberOfNights}
                  routes={procuredOffer?.routes}
                  destinationCode={
                    propertyContent.destination.searchConfig?.destinationCode
                  }
                  travelDateRange={procuredOffer?.travelDateRange}
                />
              )}
            />
          )}
        </FlexGrow>
        {isNotSelectable ? (
          <UnavailableMessage isAlternativeModal={isAlternativeModal} />
        ) : (
          <Flex gap="16px" alignItems="flex-end" flexDirection="column">
            {isPreSearch && leadInOffer && (
              <LeadInOffer
                originName={originName}
                route={route}
                offer={leadInOffer}
              />
            )}
            {!isPreSearch && offer && (
              <OfferPrice originName={originName} offer={offer} />
            )}
            {onSelectPackageOption && (
              <Button
                dialogue
                noBorder
                block
                width={['100%', null, '200px']}
                onClick={handleSelectPackageOption}
              >
                Select and continue
              </Button>
            )}
          </Flex>
        )}
      </Flex>
    </Box>
  );
};

export default PackageOptionCard;
